import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { EventApi, EventContentArg, EventInput } from '@fullcalendar/core'
import { addDays, addMinutes, isAfter, isBefore, isEqual, parse, parseISO, startOfDay, subMinutes } from 'date-fns'
import { format, formatInTimeZone, utcToZonedTime } from 'date-fns-tz'
import { isEmpty, isNil } from 'lodash-es'
import styled from 'styled-components/native'

import { isSessionOrSlot } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  BuiltEvent,
  DEFAULT_SESSION_TYPE_BY_MODE,
  EventExtendedProps,
  getNpSessionDuration,
  getNpSessionTypeFromBookingType,
  NpSessionType,
  SessionTypeSelectorMode,
  toBookingType,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { FlexibleTimeRangeSelector, formatTimeForParsing } from './FlexibleTimeRangeSelector'
import { ProviderCalendarDateInput } from './ProviderCalendarDateInput'
import { ProviderCalendarSessionTypeSelector } from './ProviderCalendarSessionTypeSelector'
import { ProviderCalendarTimeSlotSelector } from './ProviderCalendarTimeSlotSelector'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { PlusIcon } from '../../atoms/icons/PlusIcon'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '../../atoms/tooltip/Tooltip'
import { useCalendarSlots } from '../../hooks/useCalendarSlots'
import { useLocaleDateFormat } from '../../hooks/useLocaleDateFormat'
import { InlineWarningBanner } from '../../molecules/inlineWarningBanner/InlineWarningBanner'
import { ProviderCalendarModalitySelector } from '../../molecules/providerCalendarModalitySelector/ProviderCalendarModalitySelector'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const TIME_FORMAT = 'hh:mm a'
const DEFAULT_SESSION_DURATION = 50
const FLEXIBLE_TIME_FORMAT = 'h:mm a'

type GetEvent = { startDate: string; endDate: string; title?: string }

const TimeSelectorsTitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['2px'],
  marginBottom: theme.spacing['8px'],
}))

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
  width: '100%',
}))

const TimeSlotContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['8px'],
}))

export type ProviderCalendarDateSlotSelectionContentProps = {
  addresses?: Address[] | null
  defaultVideoPlatform?: VIDEO_PLATFORM | null
  eventToEdit?: EventContentArg | null
  getEvents?: (params: GetEvent) => Promise<EventInput[]>
  flexibleAvailabilityWindows?: boolean
  initialEvents?: EventApi[] | null
  inPersonPreference?: boolean
  splitVideoOptions?: boolean
  onValidityChange?: (valid: boolean) => void
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  showModalitySelection?: boolean
  slotMaxTime?: string
  slotMinTime?: string
  videoPreference?: boolean
  isNPSession?: boolean
  /** Days to prevent from being selected in date input (0=Sunday, 6=Saturday) */
  hiddenDays?: number[]
}

export type ProviderCalendarDateSlotSelectionContentRef = {
  buildPayload: () => BuiltEvent[]
  isValid: () => boolean
  reset: () => void
}

export const ProviderCalendarDateSlotSelectionContent = forwardRef<
  ProviderCalendarDateSlotSelectionContentRef,
  ProviderCalendarDateSlotSelectionContentProps
>(
  (
    {
      addresses,
      defaultVideoPlatform,
      eventToEdit,
      flexibleAvailabilityWindows = false,
      getEvents,
      initialEvents,
      inPersonPreference,
      splitVideoOptions,
      onValidityChange,
      rangeToAdd,
      showModalitySelection,
      slotMaxTime = '22:00:00',
      slotMinTime = '07:00:00',
      videoPreference,
      isNPSession,
      hiddenDays,
    },
    ref,
  ) => {
    const { formatMessage } = useIntl()

    const localeDateFormat = useLocaleDateFormat()
    const hasAddresses = !!addresses && addresses?.length > 0
    // Determine if we should show the modality section to the provider, sanity check their modality options + flag
    const hasValidModalityConfiguration =
      showModalitySelection && (videoPreference || (inPersonPreference && hasAddresses))

    const [isValidDateSelection, setValidDateSelection] = useState(true)
    const [date, setDate] = useState<string | null>(null)
    const [initialTime, setInitialTime] = useState<string | undefined>(undefined)
    const [selectedLocation, setSelectedLocation] = useState<Address | undefined>(
      hasAddresses ? addresses[0] : undefined,
    )
    const [inPersonModality, setInPersonModality] = useState<boolean>(!!inPersonPreference && hasAddresses)
    const [videoModality, setVideoModality] = useState<boolean>(!!videoPreference)
    const [selectedVideoPlatform, setSelectedVideoPlatform] = useState<VIDEO_PLATFORM>(
      defaultVideoPlatform ?? DEFAULT_VIDEO_PLATFORM,
    )

    // For flexible availability windows (Core Care)
    const [flexibleStartTime, setFlexibleStartTime] = useState<string>('')
    const [flexibleEndTime, setFlexibleEndTime] = useState<string>('')

    const eventToEditStartTime = useMemo(
      () =>
        eventToEdit
          ? parse(eventToEdit.event.startStr.replace('Z', ''), "yyyy-MM-dd'T'HH:mm:ss", new Date())
          : undefined,
      [eventToEdit],
    )
    const { isCoreCareNPIntakeSessionEnabled } = useFlags()
    const showSessionTypeSelector = isNPSession || isCoreCareNPIntakeSessionEnabled
    const selectorMode = isNPSession ? SessionTypeSelectorMode.NP : SessionTypeSelectorMode.CORE_CARE
    const initialNpSessionType = useMemo(
      () =>
        getNpSessionTypeFromBookingType(eventToEdit?.event.extendedProps?.booking_type) ??
        DEFAULT_SESSION_TYPE_BY_MODE[selectorMode],
      [eventToEdit, selectorMode],
    )
    const [npSessionType, setNpSessionType] = useState<NpSessionType | undefined>(
      showSessionTypeSelector ? initialNpSessionType : undefined,
    )
    const onChangeSessionType = (type: NpSessionType | undefined) => setNpSessionType(type)
    const sessionDuration = npSessionType ? getNpSessionDuration(npSessionType) : DEFAULT_SESSION_DURATION
    const isIntakeSelected = isCoreCareNPIntakeSessionEnabled && npSessionType === NpSessionType.NP_CLINICAL_INTAKE
    const stepMinutes = isNPSession || isIntakeSelected ? 15 : undefined
    const useFlexibleWindows = flexibleAvailabilityWindows && !isIntakeSelected

    const {
      selectedStartTimes,
      setSelectedStartTimes,
      setSelectedDateTimes,
      holdDurationMinutes,
      allAvailableTimeSlots,
      blockedTimeSlotValues,
      remainingAvailableTimeslots,
      getNextTimeSlot,
    } = useCalendarSlots({
      sessionDuration,
      slotMinTime,
      slotMaxTime,
      date,
      getEvents,
      stepMinutes,
      excludeStartTime: initialTime,
    })

    useEffect(() => {
      if (eventToEditStartTime) {
        setDate(format(eventToEditStartTime, localeDateFormat))
        setInitialTime(format(eventToEditStartTime, TIME_FORMAT))
        setSelectedStartTimes([format(eventToEditStartTime, TIME_FORMAT)])
      }
    }, [eventToEditStartTime, localeDateFormat, setSelectedStartTimes])

    useEffect(() => {
      if (eventToEdit && useFlexibleWindows) {
        const startTime = parse(eventToEdit.event.startStr.replace('Z', ''), "yyyy-MM-dd'T'HH:mm:ss", new Date())
        const endTime = parse(eventToEdit.event.endStr.replace('Z', ''), "yyyy-MM-dd'T'HH:mm:ss", new Date())
        setDate(format(startTime, localeDateFormat))
        setFlexibleStartTime(formatTimeForParsing(startTime))
        setFlexibleEndTime(formatTimeForParsing(endTime))
      }
    }, [eventToEdit, useFlexibleWindows, localeDateFormat, setDate, setFlexibleStartTime, setFlexibleEndTime])

    useEffect(() => {
      if (showModalitySelection && eventToEdit?.event.extendedProps.modality_availability) {
        setVideoModality(eventToEdit?.event.extendedProps.modality_availability.video.available)
        setInPersonModality(eventToEdit?.event.extendedProps.modality_availability.in_person.available)
        if (eventToEdit?.event.extendedProps.modality_availability.in_person.location) {
          setSelectedLocation(eventToEdit?.event.extendedProps.modality_availability.in_person.location)
        }
        const platform = eventToEdit?.event.extendedProps.modality_availability.video.platform
        if (platform && Object.values(VIDEO_PLATFORM).includes(platform)) {
          setSelectedVideoPlatform(platform)
        }
      }
    }, [eventToEdit, showModalitySelection])

    useEffect(() => {
      if (!rangeToAdd && !eventToEdit && !date) {
        setDate(format(new Date(), localeDateFormat))
      }
    }, [date, eventToEdit, localeDateFormat, rangeToAdd])

    useEffect(() => {
      const initialDate = eventToEditStartTime ?? new Date()
      const formattedEvents = initialEvents
        ?.filter(
          (e) =>
            isSessionOrSlot(e.extendedProps as EventExtendedProps) &&
            isAfter(new Date(e.startStr), startOfDay(initialDate)) &&
            isBefore(new Date(e.endStr), startOfDay(addDays(initialDate, 1))),
        )
        .map((e) => ({
          startTime: format(utcToZonedTime(parseISO(e.startStr), 'UTC'), TIME_FORMAT),
          endTime: format(utcToZonedTime(parseISO(e.endStr), 'UTC'), TIME_FORMAT),
        }))
      setSelectedDateTimes(formattedEvents)
    }, [eventToEditStartTime, initialEvents, setSelectedDateTimes])

    useEffect(() => {
      if (rangeToAdd && !useFlexibleWindows) {
        const rangeStartTime = new Date(rangeToAdd.startLocal)
        let rangeEndTime = new Date(rangeToAdd.endLocal)
        // startLocal/endLocal are "fake UTC"; see toFakeUTCISOString docs
        setDate(formatInTimeZone(rangeStartTime, 'UTC', localeDateFormat))
        let currentTime = rangeStartTime
        const initialSelectedTimeSlots: string[] = []
        if (isAfter(currentTime, subMinutes(rangeEndTime, holdDurationMinutes))) {
          rangeEndTime = addMinutes(rangeStartTime, holdDurationMinutes)
        }
        while (
          isBefore(currentTime, subMinutes(rangeEndTime, holdDurationMinutes)) ||
          isEqual(currentTime, subMinutes(rangeEndTime, holdDurationMinutes))
        ) {
          initialSelectedTimeSlots.push(formatInTimeZone(currentTime, 'UTC', TIME_FORMAT))
          currentTime = addMinutes(currentTime, holdDurationMinutes)
        }
        setSelectedStartTimes(initialSelectedTimeSlots.length > 0 ? initialSelectedTimeSlots : [''])
      }
    }, [
      useFlexibleWindows,
      holdDurationMinutes,
      localeDateFormat,
      npSessionType,
      rangeToAdd,
      setDate,
      setSelectedStartTimes,
    ])

    useEffect(() => {
      if (rangeToAdd && useFlexibleWindows) {
        const rangeStartTime = new Date(rangeToAdd.startLocal)
        const rangeEndTime = new Date(rangeToAdd.endLocal)
        // startLocal/endLocal are "fake UTC"; see toFakeUTCISOString docs
        setDate(formatInTimeZone(rangeStartTime, 'UTC', localeDateFormat))
        setFlexibleStartTime(formatTimeForParsing(rangeStartTime, true))
        setFlexibleEndTime(formatTimeForParsing(rangeEndTime, true))
      }
    }, [useFlexibleWindows, localeDateFormat, rangeToAdd, setDate, setFlexibleStartTime, setFlexibleEndTime])

    const hasValidTimes = useMemo(() => {
      if (useFlexibleWindows) {
        return !isEmpty(flexibleStartTime) && !isEmpty(flexibleEndTime)
      }
      return selectedStartTimes.filter((t) => !isEmpty(t)).length > 0
    }, [useFlexibleWindows, flexibleStartTime, flexibleEndTime, selectedStartTimes])

    const hasValidModality = useMemo(() => {
      // If the provider doesn't have this feature enabled or lacks valid modality options, this should return true
      if (!hasValidModalityConfiguration) {
        return true
      }
      if (!!inPersonPreference && !!videoPreference) {
        return inPersonModality || videoModality
      }
      if (!!inPersonPreference) {
        return inPersonModality
      }
      return videoModality
    }, [hasValidModalityConfiguration, inPersonModality, inPersonPreference, videoModality, videoPreference])

    useEffect(() => {
      onValidityChange?.(isValidDateSelection && hasValidTimes && hasValidModality)
    }, [isValidDateSelection, hasValidTimes, hasValidModality, onValidityChange])

    useImperativeHandle(
      ref,
      () => ({
        buildPayload: () => {
          if (!date) {
            return []
          }
          const modality =
            showModalitySelection && (videoModality || (inPersonModality && !!selectedLocation))
              ? {
                  video: {
                    available: videoModality,
                    ...(splitVideoOptions && videoModality ? { platform: selectedVideoPlatform } : {}),
                  },
                  in_person: {
                    available: inPersonModality && !!selectedLocation,
                    location:
                      inPersonModality && !!selectedLocation
                        ? {
                            id: selectedLocation?.id,
                            city: selectedLocation?.city,
                            country: selectedLocation?.country,
                            state: selectedLocation?.state,
                            street1: selectedLocation?.street1,
                            street2: selectedLocation?.street2,
                            zipcode: selectedLocation?.zipcode,
                          }
                        : undefined,
                  },
                }
              : undefined
          if (useFlexibleWindows) {
            if (!flexibleStartTime || !flexibleEndTime) {
              return []
            }
            const dateBase = parse(date, localeDateFormat, new Date())
            const startTime = parse(flexibleStartTime, FLEXIBLE_TIME_FORMAT, dateBase)
            let endTime = parse(flexibleEndTime, FLEXIBLE_TIME_FORMAT, dateBase)
            // Handle midnight (12:00 AM) - it parses to start of day, but should be end of day
            if (isBefore(endTime, startTime)) {
              endTime = addDays(endTime, 1)
            }
            return [
              {
                startTime: format(startTime, "yyyy-MM-dd'T'HH:mm:ss.SSSX", { timeZone: 'UTC' }),
                endTime: format(endTime, "yyyy-MM-dd'T'HH:mm:ss.SSSX", { timeZone: 'UTC' }),
                modality,
                // Omit booking_type when the selector is hidden; null clears a previous NP type in Core Care mode
                ...(showSessionTypeSelector && { booking_type: toBookingType(npSessionType) }),
              },
            ]
          }
          return selectedStartTimes
            .filter((t) => !isEmpty(t))
            .map((selected) => {
              const startTime = parse(selected, TIME_FORMAT, parse(date, localeDateFormat, new Date()))
              return {
                startTime: format(startTime, "yyyy-MM-dd'T'HH:mm:ss.SSSX", { timeZone: 'UTC' }),
                endTime: format(addMinutes(startTime, holdDurationMinutes), "yyyy-MM-dd'T'HH:mm:ss.SSSX", {
                  timeZone: 'UTC',
                }),
                modality,
                ...(showSessionTypeSelector && { booking_type: toBookingType(npSessionType) }),
              }
            })
        },
        isValid: () => isValidDateSelection && hasValidTimes && hasValidModality,
        reset: () => {
          setSelectedStartTimes([''])
          setDate(null)
          setVideoModality(!!videoPreference)
          setInPersonModality(!!inPersonPreference && hasAddresses)
          setSelectedLocation(hasAddresses ? addresses[0] : undefined)
          setFlexibleStartTime('')
          setFlexibleEndTime('')
          setSelectedVideoPlatform(defaultVideoPlatform ?? DEFAULT_VIDEO_PLATFORM)
        },
      }),
      [
        addresses,
        date,
        defaultVideoPlatform,
        useFlexibleWindows,
        flexibleEndTime,
        flexibleStartTime,
        hasAddresses,
        hasValidTimes,
        hasValidModality,
        holdDurationMinutes,
        inPersonModality,
        inPersonPreference,
        splitVideoOptions,
        isValidDateSelection,
        localeDateFormat,
        showSessionTypeSelector,
        isNPSession,
        npSessionType,
        selectedLocation,
        selectedStartTimes,
        selectedVideoPlatform,
        setSelectedStartTimes,
        showModalitySelection,
        videoModality,
        videoPreference,
      ],
    )

    const selectedDateParsed = useMemo(
      () => (date ? parse(date, localeDateFormat, new Date()) : new Date()),
      [date, localeDateFormat],
    )

    return (
      <Container>
        {!useFlexibleWindows && allAvailableTimeSlots.length === 0 && (
          <InlineWarningBanner
            testId={tID('ProviderCalendarBaseSlotSelectionModal-noAvailabilityBanner')}
            text={formatMessage({
              defaultMessage: 'No availability today. Choose another day',
              description:
                "Message to indicate that there are no timeslots that are bookable for the chosen day in the provider's calendar.",
            })}
          />
        )}
        <ProviderCalendarSessionTypeSelector
          show={showSessionTypeSelector}
          onChange={onChangeSessionType}
          initialValue={initialNpSessionType}
          mode={selectorMode}
        />
        <ProviderCalendarDateInput
          value={date}
          onChange={setDate}
          onValidityChange={setValidDateSelection}
          preventOpenOnFocus={true}
          hiddenDays={hiddenDays}
        />

        {useFlexibleWindows ? (
          <FlexibleTimeRangeSelector
            slotMinTime={slotMinTime}
            slotMaxTime={slotMaxTime}
            selectedDate={selectedDateParsed}
            selectedStartTime={flexibleStartTime}
            selectedEndTime={flexibleEndTime}
            onStartTimeChange={setFlexibleStartTime}
            onEndTimeChange={setFlexibleEndTime}
          />
        ) : (
          <>
            <View>
              <TimeSelectorsTitleContainer>
                <BodyText
                  text={<FormattedMessage defaultMessage='Time' description='Label for time slot selection section' />}
                />
                <Tooltip
                  accessibilityLabel={formatMessage({
                    defaultMessage: 'Session and hold duration information',
                    description: 'Accessibility label for tooltip showing session and hold duration',
                  })}
                  content={
                    <FormattedMessage
                      defaultMessage='{sessionDuration}min sessions • {holdDurationHours} hour holds'
                      description='Tooltip explaining session duration and hold duration for availability slots'
                      values={{ sessionDuration, holdDurationHours: holdDurationMinutes / 60 }}
                    />
                  }
                  placement='top'
                  triggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO, isFilled: false, size: 16 }}
                  hoverEnabled
                />
              </TimeSelectorsTitleContainer>
              <TimeSlotContainer>
                {selectedStartTimes.map((selectedStartTime, index) => (
                  <ProviderCalendarTimeSlotSelector
                    key={index}
                    allOptions={allAvailableTimeSlots}
                    blockedTimeSlotValues={blockedTimeSlotValues}
                    index={index}
                    selectedOption={selectedStartTime}
                    selectedOptionList={selectedStartTimes}
                    setSelectedOptionList={setSelectedStartTimes}
                    showDeleteIcon={index !== 0}
                    singleTimeSelector={selectedStartTimes.length === 1}
                  />
                ))}
              </TimeSlotContainer>
            </View>
            {isNil(eventToEdit) && remainingAvailableTimeslots.length > 0 && (
              <TextButton
                text={
                  <FormattedMessage
                    defaultMessage='Add another time'
                    description='Button label for provider to add another time slot selection'
                  />
                }
                leftIcon={<PlusIcon />}
                onPress={() => setSelectedStartTimes((prev) => [...prev, getNextTimeSlot()])}
                testID={tID('ProviderCalenderSlotSelectionModal-addSlotButton')}
              />
            )}
          </>
        )}

        {hasValidModalityConfiguration && (
          <ProviderCalendarModalitySelector
            addresses={addresses}
            inPersonModality={inPersonModality}
            inPersonPreference={inPersonPreference}
            splitVideoOptions={splitVideoOptions}
            selectedLocation={selectedLocation}
            selectedVideoPlatform={selectedVideoPlatform}
            setInPersonModality={setInPersonModality}
            setSelectedLocation={setSelectedLocation}
            setSelectedVideoPlatform={setSelectedVideoPlatform}
            setVideoModality={setVideoModality}
            videoModality={videoModality}
            videoPreference={videoPreference}
          />
        )}
      </Container>
    )
  },
)
